import { AxiosError, AxiosResponse } from 'axios';
import { SESSION_KEY } from 'hooks/context/customer/customer';
import { isAxiosError } from 'util/axiosHelpers';
import { LocalStorage } from 'util/Storage';
import { CUSTOM_ERROR, CustomErrorMessage, ValidationError } from 'components/Input.type';
import React, { ReactNode } from 'react';

const GENERIC_ERROR_TEXT = 'UNKNOWN';

type AnyError = any | string | AxiosError | AxiosResponse | undefined;

export const parseAuthErrorMessage = (error: AnyError) => {
  if (!error) return GENERIC_ERROR_TEXT;
  if (typeof error === 'string') return error;

  const errorResponse = isAxiosError(error) ? error.response : error;

  if (typeof errorResponse?.data?.errorCondition === 'string') return errorResponse.data.errorCondition;

  return GENERIC_ERROR_TEXT;
};

export const invalidateSession = (error: any) => {
  const errorResponse = isAxiosError(error) ? error.response : error;

  if (errorResponse?.status === 401) LocalStorage.tryRemoveItem(SESSION_KEY);
};

type errorMessageOptions = {
  // The HTML5 constraint validation errors list
  errors: ValidationError[];
  // The validation message provided by the browser and HTML5 constraints API
  validationMessage?: string;
  // The passed in error constant OR error code map lookup
  errorMessage?: CustomErrorMessage;
  // Flag determining if there is a custom error. Can also be used as a simple replacement to customErrorMessage
  customError?: boolean | string | null;
}

export function determineErrorMessage({ errors, validationMessage, errorMessage, customError }: errorMessageOptions): ReactNode {
  if (!errors.length) return '';

  const firstError = errors[0];

  let errorText: ReactNode = '';
  if (errorMessage) {
    errorText = React.isValidElement(errorMessage) || typeof errorMessage === 'string'
      ? errorMessage
      : (errorMessage as Record<keyof ValidityState, ReactNode>)[firstError];
  }

  // If we couldn't determine the errorMessage from errorMessage use customError if it is a string
  if (!errorText && typeof customError === 'string') {
    errorText = customError;
  }

  // If the error isn't custom then use the browser's error message
  if (!errorText && validationMessage && validationMessage !== CUSTOM_ERROR) {
    errorText = validationMessage;
  }

  return errorText || '';
}
